import { DriveState } from './drive'
import { EventsState } from './event'
import { TeamState } from './team'
import { UserState } from './user'
import { WorkstationState } from './workstation'

// RootState types
export interface RootState {
  version: string
  requests: {
    // user
    login: RequestInfo
    logOut: RequestInfo
    social: RequestInfo
    registration: RequestInfo
    profileUpdate: RequestInfo
    billingUpdate: RequestInfo
    paymentMethodUpdate: RequestInfo
    creditDataUpdate: RequestInfo
    storageTierUpdate: RequestInfo
    notificationsAboutTeamsFeatureToggle: RequestInfo
    passwordChange: RequestInfo
    passwordReset: RequestInfo
    subscriptionCancel: RequestInfo
    canceledSubscriptionContinue: RequestInfo
    getAdminUsers: RequestInfo
    bugReport: RequestInfo
    contactSupport: RequestInfo
    // credits
    redeemCode: RequestInfo
    addCredits: RequestInfo
    refreshCredits: RequestInfo
    // resources
    getWorkstations: RequestInfo
    createWorkstation: RequestInfo
    createDrive: RequestInfo
    createResources: RequestInfo
    setupOnboarding: RequestInfo
    getWorkstationStatus: RequestInfo
    getDrives: RequestInfo
    getEvents: RequestInfo
    // teams
    getInvites: RequestInfo
    getTeamMembers: RequestInfo
    inviteTeamMember: RequestInfo
    cancelInvite: RequestInfo
    acceptInvite: RequestInfo
    assignResource: RequestInfo
    currentResourceMembers: RequestInfo
    removeMember: RequestInfo
    // browser
    loadFiles: RequestInfo
    directoryNavigate: RequestInfo
    directoryCreate: RequestInfo
    itemsDelete: RequestInfo
    itemRename: RequestInfo
    // transcoding
    fileTranscode: RequestInfo
    listTranscodingJobs: RequestInfo
    deleteTranscodingJob: RequestInfo
    // links
    linkShare: RequestInfo
    linkUnshare: RequestInfo
    linkList: RequestInfo
    // config
    getConfig: RequestInfo
  }
  user: UserState
  workstation: WorkstationState
  drive: DriveState
  event: EventsState
  team: TeamState
  theme: Theme
}

export type Theme = 'dark' | 'bright'

export type RequestStatus = 'none' | 'pending' | 'successful' | 'failed'
export type RequestName =
  // user
  | 'login'
  | 'logOut'
  | 'social'
  | 'registration'
  | 'profileUpdate'
  | 'billingUpdate'
  | 'paymentMethodUpdate'
  | 'creditDataUpdate'
  | 'storageTierUpdate'
  | 'passwordChange'
  | 'passwordReset'
  | 'subscriptionCancel'
  | 'canceledSubscriptionContinue'
  | 'getAdminUsers'
  | 'bugReport'
  | 'contactSupport'

  // credits
  | 'redeemCode'
  | 'addCredits'
  | 'refreshCredits'

  // resources
  | 'getWorkstations'
  | 'createWorkstation'
  | 'createDrive'
  | 'getWorkstationStatus'
  | 'getDrives'
  | 'createResources'
  | 'setupOnboarding'
  | 'getEvents'
  // teams
  | 'getInvites'
  | 'getTeamMembers'
  | 'inviteTeamMember'
  | 'cancelInvite'
  | 'acceptInvite'
  | 'assignResource'
  | 'currentResourceMembers'
  | 'removeMember'
  // browser
  | 'loadFiles'
  | 'directoryNavigate'
  | 'directoryCreate'
  | 'itemsDelete'
  | 'itemRename'
  | 'fileTranscode'
  | 'listTranscodingJobs'
  | 'deleteTranscodingJob'
  // links
  | 'linkShare'
  | 'linkUnshare'
  | 'linkList'
  // config
  | 'getConfig'

export type RequestPayload = { status: RequestStatus; error?: string }
export class RequestInfo {
  status: RequestStatus = 'none'
  error?: string
  constructor(payload?: { status: RequestStatus; error?: string }) {
    this.status = payload ? payload.status : 'none'
    this.error = payload ? payload.error : undefined
  }
  get pending(): boolean {
    return this.status === 'pending'
  }
  get successful(): boolean {
    return this.status === 'successful'
  }
  get failed(): boolean {
    return this.status === 'failed'
  }
}
export class RequestUpdate {
  name: RequestName
  info: RequestInfo
  constructor(name: RequestName, status: RequestStatus, error?: string) {
    this.name = name
    this.info = new RequestInfo({ status, error })
  }
}

export type SelectedIds = { [key: string]: boolean }
